import React, { useCallback, useEffect, useRef } from 'react';
import { TypedMemo } from '../../common/types';
import { LoadingView } from '../../common/views/LoadingView';
import { ErrorView } from '../../common/views/ErrorView/ErrorView';
import { SMLAnalytics } from '../../analytics/sml/analytics';
import { useSMLRouter } from '../../../routing/sml/hooks';
import { takeSignupLandingRoute } from '../signupLanding';
import { SMLRoutes } from '../../../routing/sml/SMLRoutes';
import { useOnboardingState } from '../useOnboardingState';
import { useSyncSignupIntent } from '../useSyncSignupIntent';
import { onboardingIntentOf } from '../signupIntent';
import { useSaveVisitorLocale } from '../../app/locale/useSaveVisitorLocale';
import { useSeenWelcomeRedirect } from './useSeenWelcomeRedirect';
import { WelcomeTopBar } from './WelcomeTopBar';
import { OwnerWelcome } from './OwnerWelcome';
import { MemberWelcome } from './MemberWelcome';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole welcome page; each part uses its own slice of it */
import styles from './WelcomeView.module.scss';
/* eslint-enable css-modules/no-unused-class */

export interface WelcomeViewProps {
  /** "Show me an example". The route (`WelcomeWithTour`) passes `useTour().start`. With none, it opens Home. */
  onExample?: () => void;
}

/**
 * `/app/welcome`: the first page after signup, and once for every existing user
 * (design: onboarding a1-a8, docs/superpowers/design-reference/music24-onboarding/a Welcome.dc.html).
 * A full page with no sidebar, like the sign-in page.
 */
const WelcomeViewComponent: React.FC<WelcomeViewProps> = ({ onExample }) => {
  const router = useSMLRouter();
  const { state, isError, error, refetch } = useOnboardingState();
  useSyncSignupIntent();
  // The language picked on music24.com or on the sign-in page goes to the new account.
  useSaveVisitorLocale();
  // The shell of Home can send and clear the stored intent before this page mounts. The intent of
  // this page load fills the gap until the server answer has it.
  const intent = onboardingIntentOf(state);

  const isLeaving = useSeenWelcomeRedirect(state);

  const trackedRef = useRef(false);
  useEffect(() => {
    // A person who already saw the page goes to Home: that is no view of the welcome page.
    if (state === undefined || trackedRef.current || !state.showWelcome) {
      return;
    }
    trackedRef.current = true;
    SMLAnalytics.track('onboarding_welcome_shown', { intent }).catch(console.error);
  }, [intent, state]);

  // A signup from music24.com/pitch lands on Outreach → Pitch instead of Home (`signupLanding.ts`).
  const openHome = useCallback(() => router.replace(takeSignupLandingRoute() ?? SMLRoutes.home), [router]);
  const showExample = onExample ?? openHome;
  const addSong = useCallback(() => router.replace(SMLRoutes.addSong), [router]);

  const renderContent = () => {
    if (state === undefined && isError) {
      return (
        <ErrorView
          error={error ?? undefined}
          retry={() => {
            refetch().catch(console.error);
          }}
        />
      );
    }
    if (state === undefined || isLeaving) {
      return <LoadingView />;
    }
    if (state.isMember) {
      return <MemberWelcome onExample={showExample} onOpenWorkspace={openHome} />;
    }
    return <OwnerWelcome state={state} intent={intent} onExample={showExample} onAddSong={addSong} refetch={refetch} />;
  };

  return (
    <div className={styles.page} data-m24-full-page="welcome">
      <WelcomeTopBar />
      {renderContent()}
    </div>
  );
};

export const WelcomeView = TypedMemo(WelcomeViewComponent);
